import {getPixelsFromRem, Show, VStack} from 'platform/foundation';
import {useTheme} from 'styled-components';

import {useId, useMemo, useRef} from 'react';
import ReactSelect, {createFilter, OnChangeValue, type SelectInstance} from 'react-select';

import {defaultTo, isNil} from 'ramda';

import {Nullish, suffixTestId} from 'shared/web';

import {MENU_MAX_HEIGHT} from '../../../constants/menuMaxHeight';
import {OptionTypeBase} from '../../../types/OptionTypeBase';
import {HelperText} from '../../HelperText/HelperText';
import {Label} from '../../Label/Label';
import {useTranslationContext} from '../../TranslationProvider/TranslationContext';
import type {ChoiceProps as BaseChoiceProps} from '../Choice';
import {ChoiceProvider} from '../components/ChoiceProvider';
import {GetItemImage, selectComponents} from '../utils/avatarChoiceComponents';
import {formatOptionFieldLabel} from '../utils/formatOptionFieldLabel';
import {getChoiceCSS} from '../utils/getChoiceCSS';
import {getFlattenedOptions} from '../utils/getFlattenedOptions';
import {reactSelectStyles} from '../utils/reactSelectStyles';
import {selectOption} from '../utils/selectOption';

type AvatarChoiceProps<ValueType> = BaseChoiceProps<ValueType> & {
  getItemImage: GetItemImage<ValueType>;
};

export function AvatarChoice<ValueType>(props: AvatarChoiceProps<ValueType>) {
  const selectRef = useRef<SelectInstance<OptionTypeBase<ValueType | Nullish>, false> | null>(null);
  const t = useTranslationContext();
  const theme = useTheme();
  const id = useId();

  const isInvalid = props.isInvalid || !!props.errorMessage;

  const onSelectMount = (
    select: SelectInstance<OptionTypeBase<ValueType | Nullish>, false> | null
  ) => {
    if (select) {
      selectOption(select);
    }

    selectRef.current = select;
  };

  const onMenuOpen = () => {
    if (selectRef.current) {
      selectOption(selectRef.current);
    }
  };

  const handleChange = (options: OnChangeValue<OptionTypeBase<ValueType | Nullish>, false>) => {
    if (!options) {
      props.onChange?.(null);
      return;
    }

    props.onChange?.(options.value ?? null);
  };

  const getOptionsByValue = (value: ValueType | Nullish) => {
    if (isNil(value)) {
      return null;
    }

    const flatOptions = getFlattenedOptions(props.options);

    return flatOptions?.find((o) => o.value === value) ?? null;
  };

  // This is a workaround for an issue in react-select, when using custom components
  // https://github.com/JedWatson/react-select/issues/5489
  const components = useMemo(
    () => ({...selectComponents<ValueType, false>({getItemImage: props.getItemImage})}),
    []
  );

  const defaultFilterOption = useMemo(
    () =>
      createFilter({
        stringify: (option) => `${option.label}`,
        ignoreCase: true,
      }),
    []
  );

  return (
    <div
      onKeyDown={(event) => {
        // We want to stop propagation when the choice is located in a modal, so that closing the option list doesn't close the modal itself.
        if (event.key === 'Escape') {
          event.stopPropagation();
        }
      }}
    >
      <VStack>
        <Label
          id={id}
          isRequired={props.isRequired}
          tooltip={props.tooltip}
          isRecommended={props.isRecommended}
          actions={props.labelActions}
          data-testid={suffixTestId('label', props)}
        >
          {props.label}
        </Label>
        <ChoiceProvider
          options={{
            'data-testid': props['data-testid'],
            'data-name': props.name,
            hasOptionCheckbox: props.hasOptionCheckbox,
          }}
        >
          <ReactSelect
            inputId={id}
            autoFocus={props.autoFocus}
            formatOptionLabel={props.formatOptionLabel ?? formatOptionFieldLabel}
            defaultValue={getOptionsByValue(props.defaultValue)}
            ref={onSelectMount}
            menuPortalTarget={
              props.menuInPortal && typeof document !== 'undefined' ? document.body : undefined
            }
            value={getOptionsByValue(props.value)}
            name={props.name}
            placeholder={defaultTo(t('general.labels.select'), props.placeholder)}
            onInputChange={() => {
              /**
               * workaround
               * https://github.com/JedWatson/react-select/issues/3832
               */
              if (props.menuIsOpen) {
                selectRef?.current?.inputRef?.focus();
              }
            }}
            onFocus={props.onFocus}
            onBlur={props.onBlur}
            onChange={handleChange}
            isDisabled={props.isDisabled}
            options={props.options}
            isClearable={!props.isNotClearable}
            isSearchable={props.isSearchable ?? true}
            components={components}
            hideSelectedOptions={false}
            tabSelectsValue={false}
            menuPlacement={props.menuPlacement ?? 'auto'}
            maxMenuHeight={getPixelsFromRem(props.maxMenuHeight ?? MENU_MAX_HEIGHT)}
            styles={reactSelectStyles<ValueType, false>(theme, props.horizontalMenuPlacement)}
            onMenuOpen={onMenuOpen}
            noOptionsMessage={props.noOptionsMessage}
            closeMenuOnScroll={props.closeMenuOnScroll}
            closeMenuOnSelect={props.closeMenuOnSelect}
            isLoading={props.isLoading}
            filterOption={props.filterOption ?? defaultFilterOption}
            // Styling ReactSelect is problematic so we need to use the css prop
            // eslint-disable-next-line react/forbid-component-props
            css={getChoiceCSS(isInvalid, props.isDisabled, props.isRecommended)}
          />
        </ChoiceProvider>
        <Show when={props.errorMessage ?? props.helperText}>
          <HelperText
            errorMessage={props.errorMessage}
            helperText={props.helperText}
            data-testid={suffixTestId('helper', props)}
          />
        </Show>
      </VStack>
    </div>
  );
}
